/**
 * The company layout: every prototype screen renders inside the suite's
 * AppShell. `aud="platform"` is the one audience that makes no network call
 * (no tenant branding, no server preferences). The company switcher sits in
 * the persona slot, the one slot that stays on phones; quick create and the
 * product switcher take the tenant chip's place; phones get the app (no
 * desktop-only scrim).
 */
import { useState, type JSX } from "react";
import {
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { FlaskConical } from "lucide-react";
import { Alert, AppShell, Button, EmptyState, RailTile, nonEmpty } from "@linkhr/design-system";
import { useTranslation } from "@linkhr/i18n";
import { companyNav } from "../nav";
import {
  DATASETS,
  DEFAULT_COMPANY,
  NEEDS_YOU,
  companyById,
  isCompanyId,
  userForMode,
} from "../prototype/fixtures";
import { sessionData, usePrototype } from "../prototype/store";
import { useFormat } from "../components/format";
import { PrototypePanel } from "../shell/PrototypePanel";
import { CompanySwitcher, Notifications, ProductMenu, QuickCreate } from "../shell/TopBarControls";

export const Route = createFileRoute("/prototype/c/$companyId")({
  beforeLoad: ({ params }) => {
    if (!isCompanyId(params.companyId)) {
      throw redirect({
        to: "/prototype/c/$companyId/home",
        params: { companyId: DEFAULT_COMPANY },
      });
    }
  },
  component: CompanyLayout,
});

function CompanyLayout(): JSX.Element {
  const { companyId: param } = Route.useParams();
  const companyId = isCompanyId(param) ? param : DEFAULT_COMPANY;
  const company = companyById(companyId);
  const { t, i18n } = useTranslation("accounting");
  const { locale, date } = useFormat();
  const navigate = useNavigate();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { state } = usePrototype();
  const [panelOpen, setPanelOpen] = useState(false);
  const user = userForMode(state.mode);

  const navBadges = {
    inbox: sessionData(state, companyId).intake.filter((item) => NEEDS_YOU.includes(item.state))
      .length,
    // The accountant's queue: everything not waiting on the owner.
    review: DATASETS[companyId].workItems.filter((item) => item.assignee !== "samer").length,
  };

  const banner =
    state.productState === "TRIAL" ? (
      <Alert
        variant="info"
        description={t("accounting:shell.banner.trial", { date: date(company.trialEndsOn) })}
      />
    ) : state.productState === "READ_ONLY" ? (
      <Alert variant="warning" description={t("accounting:shell.banner.read_only")} />
    ) : null;

  const lockedTitle = t("accounting:shell.locked.title");
  const page =
    state.productState === "LOCKED" ? (
      <EmptyState
        entities={nonEmpty(lockedTitle)}
        nextAction={nonEmpty(lockedTitle)}
        heading={lockedTitle}
        body={t("accounting:shell.locked.body")}
        cta={
          <Button asChild>
            <a href="/admin/">{t("accounting:shell.locked.open_launcher")}</a>
          </Button>
        }
      />
    ) : (
      <Outlet />
    );

  const panelLabel = t("accounting:proto.rail");

  return (
    <AppShell
      nav={companyNav(companyId, state.mode)}
      aud="platform"
      appName={t("accounting:shell.app_name")}
      user={{ displayName: user.name[locale], email: user.email, roles: [] }}
      tenantName={company.name[locale]}
      // The prototype has no sign-in, so there is nothing to sign out of.
      onLogout={() => undefined}
      locale={locale}
      onLocaleChange={(next) => void i18n.changeLanguage(next)}
      onNavigate={(href) => void navigate({ to: href })}
      navBasePath={router.basepath}
      activeHref={pathname}
      navBadges={navBadges}
      railBottom={
        <RailTile
          icon={FlaskConical}
          label={panelLabel}
          selected={panelOpen}
          onClick={() => setPanelOpen(true)}
        />
      }
      personaSlot={<CompanySwitcher companyId={companyId} />}
      tenantContent={
        <div className="flex items-center gap-xs">
          <QuickCreate companyId={companyId} />
          <ProductMenu />
        </div>
      }
      notificationsSlot={<Notifications companyId={companyId} />}
      desktopOnly={false}
    >
      <div className="flex flex-col gap-md">
        {banner}
        {page}
      </div>
      <PrototypePanel open={panelOpen} onOpenChange={setPanelOpen} />
    </AppShell>
  );
}
